import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../AuthContext';
import { Logo } from '../components/Brand';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';

const DEMO_GATE_PASSWORD = 'K9OS007$';
const DEMO_GATE_KEY = 'k9os_demo_gate_unlocked';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  // Demo access gate — session-scoped so the password isn't asked again on the same tab
  const [demoUnlocked, setDemoUnlocked] = useState(() =>
    typeof window !== 'undefined' && window.sessionStorage.getItem(DEMO_GATE_KEY) === '1'
  );
  const [gateOpen, setGateOpen] = useState(false);
  const [gateAttempt, setGateAttempt] = useState('');
  const [pendingDemo, setPendingDemo] = useState(null);

  // Discreet unlock trigger — visit /login?demo (or /login?demo=1) to open the
  // gate dialog. Once unlocked, the demo section stays visible for the session.
  useEffect(() => {
    if (demoUnlocked) return;
    const params = new URLSearchParams(location.search);
    if (params.has('demo')) {
      setGateOpen(true);
      setGateAttempt('');
    }
  }, [location.search, demoUnlocked]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}`);
      const dest = location.state?.from || (user.role === 'CLIENT' ? '/client' : '/admin');
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Gate every demo-account quick-fill behind K9OS007$ so it isn't
  // discoverable / usable by casual visitors of the public site.
  const quickLogin = (em, pw) => {
    if (!demoUnlocked) {
      setPendingDemo({ em, pw });
      setGateAttempt('');
      setGateOpen(true);
      return;
    }
    setEmail(em);
    setPassword(pw);
  };

  const submitGate = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (gateAttempt === DEMO_GATE_PASSWORD) {
      window.sessionStorage.setItem(DEMO_GATE_KEY, '1');
      setDemoUnlocked(true);
      setGateOpen(false);
      if (pendingDemo) {
        setEmail(pendingDemo.em);
        setPassword(pendingDemo.pw);
        setPendingDemo(null);
      }
      toast.success('Demo access unlocked for this session.');
    } else {
      toast.error('Incorrect access code.');
    }
    setGateAttempt('');
  };

  return (
    <div className='min-h-screen min-h-[100dvh] grid md:grid-cols-2 bg-[#FFF8F2] dark:bg-slate-950'>
      {/* Left brand panel — hidden on mobile */}
      <div className='hidden md:flex flex-col justify-between p-12 bg-[#0B1220] text-white relative overflow-hidden'>
        <Logo />
        <div className='relative z-10'>
          <h1 className='font-display text-4xl font-bold tracking-tight'>The dog-trainer OS.</h1>
          <p className='text-slate-300 mt-4 max-w-md'>Bookings, memberships, POS, contracts, messaging, and marketing — one login for your entire operation. Powered by K9os.</p>
        </div>
        <div className='relative z-10 text-xs text-slate-500'>© {new Date().getFullYear()} K9os App</div>
        <div className='absolute -right-20 -bottom-20 w-[400px] h-[400px] rounded-full bg-[#C8102E]/10 blur-3xl' />
      </div>

      {/* Right form area — mobile-first */}
      <div className='flex flex-col min-h-screen min-h-[100dvh] md:min-h-0'>
        {/* Mobile top bar */}
        <div className='md:hidden flex items-center justify-between px-4 pt-3 pb-2'>
          <Link to='/' aria-label='Home' className='flex items-center'>
            <Logo />
          </Link>
          <ThemeToggle size='sm' />
        </div>

        <div className='flex-1 flex items-center justify-center px-4 py-6 sm:px-6 md:p-12'>
          <Card className='w-full max-w-md mx-auto p-5 sm:p-7 md:p-8 shadow-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
            <div className='mb-5 sm:mb-6'>
              <h2 className='font-display text-2xl sm:text-3xl font-bold text-[#0B1220] dark:text-slate-50'>Sign in</h2>
              <p className='text-sm text-slate-500 dark:text-slate-400 mt-1'>Welcome back to K9os App.</p>
            </div>
            <form onSubmit={submit} className='space-y-4' autoComplete='on'>
              <div>
                <Label htmlFor='email'>Email</Label>
                <Input
                  id='email'
                  type='email'
                  autoComplete='email'
                  inputMode='email'
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='you@example.com'
                  className='h-12 text-base sm:h-11 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100'
                  data-testid='auth-email-input'
                />
              </div>
              <div>
                <Label htmlFor='password'>Password</Label>
                <Input
                  id='password'
                  type='password'
                  autoComplete='current-password'
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder='••••••••'
                  className='h-12 text-base sm:h-11 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100'
                  data-testid='auth-password-input'
                />
              </div>
              <Button
                type='submit'
                className='w-full h-12 text-base sm:h-11 sm:text-sm bg-[#1B3A87] hover:bg-[#16306F] text-white'
                disabled={loading}
                data-testid='auth-submit-button'
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
            <div className='mt-5 text-sm text-slate-600 dark:text-slate-300 text-center'>
              New here?{' '}
              <Link to='/register' className='text-[#1B3A87] dark:text-[#93C5FD] font-semibold hover:underline' data-testid='auth-register-link'>
                Create an account
              </Link>
            </div>

            {/* Demo accounts — hidden from public visitors. Unlock by
                visiting /login?demo and entering the gate password. Once
                unlocked (session-scoped), Greg (Admin) + Test Client
                quick-fill buttons appear here for internal demos. */}
            {demoUnlocked && (
              <div className='mt-6 pt-5 border-t border-slate-200 dark:border-slate-800'>
                <button
                  type='button'
                  onClick={() => setShowDemo((v) => !v)}
                  className='w-full flex items-center justify-between text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400'
                  data-testid='demo-accounts-toggle'
                >
                  <span>Demo accounts (tap to fill)</span>
                  <span className='text-slate-400'>{showDemo ? '−' : '+'}</span>
                </button>
                {showDemo && (
                  <div className='grid grid-cols-2 gap-2 text-xs mt-3'>
                    {[
                      { l: 'Greg (Admin)', em: 'greg@k9os.app', pw: 'K9OS007$', tid: 'demo-greg' },
                      { l: 'Test Client', em: 'client@jfk9.com', pw: 'Client@123', tid: 'demo-client' },
                    ].map((q) => (
                      <button
                        type='button'
                        key={q.l}
                        className='px-2 py-2 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] hover:bg-[#1B3A87]/5 dark:hover:bg-[#1B3A87]/15 text-left bg-white dark:bg-slate-800'
                        onClick={() => quickLogin(q.em, q.pw)}
                        data-testid={q.tid}
                      >
                        <div className='font-semibold'>{q.l}</div>
                        <div className='text-[10px] text-slate-500 dark:text-slate-400 truncate font-mono'>{q.em}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        <div className='md:hidden text-center text-[11px] text-slate-400 pb-4 px-4'>
          © {new Date().getFullYear()} K9os.app
        </div>
      </div>

      {/* Demo-access gate — opens when visiting /login?demo or when a demo
          button is clicked before unlock. K9OS007$ unlocks demo tools for
          the current browser session. */}
      <Dialog open={gateOpen} onOpenChange={setGateOpen}>
        <DialogContent className='sm:max-w-sm' data-testid='demo-gate-dialog'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Lock className='w-4 h-4 text-[#C8102E]' />
              Demo access
            </DialogTitle>
            <DialogDescription>
              Enter the K9os access code to unlock the demo quick-fill buttons for this session.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitGate} className='space-y-3'>
            <div>
              <Label htmlFor='gate-code'>Access code</Label>
              <Input
                id='gate-code'
                type='password'
                autoFocus
                value={gateAttempt}
                onChange={(e) => setGateAttempt(e.target.value)}
                placeholder='••••••••'
                className='h-11'
                data-testid='demo-gate-input'
              />
            </div>
            <DialogFooter className='gap-2 sm:gap-0'>
              <Button
                type='button'
                variant='ghost'
                onClick={() => { setGateOpen(false); setGateAttempt(''); setPendingDemo(null); }}
                data-testid='demo-gate-cancel'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                className='bg-[#1B3A87] hover:bg-[#16306F] text-white'
                data-testid='demo-gate-submit'
              >
                Unlock
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
